/* @refresh reload */
import { NetworkStatus } from "@apollo/client";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";

import type {
  DocumentFilter,
  TransactionFilter,
} from "@/__generated__/graphql";
import { Queries } from "@/constants";
import { useBackendQuery } from "@/hooks/useBackendQuery";

import {
  TriageOptionList,
  documentToTriageListItem,
  transactionToTriageListItem,
  type TriageOptionListItem,
} from "./TriageSuggestionsPanel";
import { type TriageResolutionTab } from "./TriageView.resolution";
import {
  useTriageDetailContext,
  type TriageChartedAccountCatalogItem,
  type TriageDetail,
} from "./TriageView.context";

const PAGE_SIZE = 25;

/** Bank txs whose journal entry is missing or not yet posted (closed). */
const TRIAGE_TRANSACTION_CATALOG_FILTER = {
  all: [
    { object: { financialAccount: { isNull: false } } },
    { openJournal: true },
  ],
} as TransactionFilter;

/** Documents with no related transaction on a posted (closed) journal entry. */
const TRIAGE_DOCUMENT_CATALOG_FILTER: DocumentFilter = {
  openJournal: true,
};

export function documentCatalogFilter(search: string): DocumentFilter {
  const term = search.trim();
  if (!term) {
    return TRIAGE_DOCUMENT_CATALOG_FILTER;
  }
  return {
    all: [
      TRIAGE_DOCUMENT_CATALOG_FILTER,
      {
        any: [
          { field: { name: { iContains: term } } },
          { field: { description: { iContains: term } } },
        ],
      },
    ],
  };
}

export function transactionCatalogFilter(search: string): TransactionFilter {
  const term = search.trim();
  if (!term) {
    return TRIAGE_TRANSACTION_CATALOG_FILTER;
  }
  return {
    all: [
      TRIAGE_TRANSACTION_CATALOG_FILTER,
      {
        any: [
          { field: { description: { iContains: term } } },
          { search: term },
        ],
      },
    ],
  };
}

function isCatalogVariableFetch(networkStatus: NetworkStatus): boolean {
  return (
    networkStatus === NetworkStatus.loading ||
    networkStatus === NetworkStatus.setVariables ||
    networkStatus === NetworkStatus.refetch
  );
}

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timeout = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timeout);
  }, [delayMs, value]);
  return debounced;
}

export function accountToListItem(
  account: TriageChartedAccountCatalogItem,
  selectedChartedAccountIdentifier: string | null,
): TriageOptionListItem {
  // Named by where it sits in the chart, down to itself - the chart's root
  // left off - and its categories under it.
  const pathWithoutRoot = account.fullPath?.slice(1) ?? [];
  return {
    key: `account:${account.identifier}`,
    sectionId: "accounts",
    selectionMode: "single",
    title:
      pathWithoutRoot.length > 0
        ? pathWithoutRoot.join(" / ")
        : (account.name ?? "Account"),
    categories: (account.categories ?? [])
      .filter((category) => Boolean(category.identifier && category.name))
      .map((category) => ({
        id: category.id,
        identifier: category.identifier,
        name: category.name ?? "",
        color: category.color ?? null,
      })),
    selected: selectedChartedAccountIdentifier === account.identifier,
  };
}

type TriageTabOptionsPanelProps = {
  triage: TriageDetail;
  entityIdentifiers: string[];
  tab: TriageResolutionTab;
  /** What is searched for, in the match panel's toolbar. */
  search: string;
};

export function TriageTabOptionsPanel({
  triage,
  entityIdentifiers,
  tab,
  search,
}: TriageTabOptionsPanelProps) {
  const {
    selections,
    activeOptionKey,
    setActiveOptionKey,
    registerCatalogChartedAccounts,
    registerCatalogDocuments,
    registerCatalogTransactions,
  } = useTriageDetailContext();
  const debouncedSearch = useDebouncedValue(search, 300);
  const [accountPage, setAccountPage] = useState(1);

  const documentsQuery = useBackendQuery(Queries.DOCUMENTS_CONNECTION_QUERY, {
    variables: {
      entityIdentifiers,
      first: PAGE_SIZE,
      filter: documentCatalogFilter(debouncedSearch),
    },
    skip: tab !== "document" || entityIdentifiers.length === 0,
    fetchPolicy: "cache-first",
    notifyOnNetworkStatusChange: true,
  });

  const chartedAccountsQuery = useBackendQuery(
    Queries.TRIAGE_CHARTED_ACCOUNT_CATALOG_QUERY,
    {
      variables: { entityIdentifiers: entityIdentifiers.slice(0, 1) },
      skip: tab !== "categorize" || entityIdentifiers.length === 0,
      fetchPolicy: "cache-first",
      notifyOnNetworkStatusChange: true,
    },
  );

  const transactionsQuery = useBackendQuery(
    Queries.TRANSACTIONS_CONNECTION_QUERY,
    {
      variables: {
        entityIdentifiers,
        first: PAGE_SIZE,
        filter: transactionCatalogFilter(debouncedSearch),
      },
      skip: tab !== "transactions" || entityIdentifiers.length === 0,
      fetchPolicy: "cache-first",
      notifyOnNetworkStatusChange: true,
    },
  );

  useEffect(() => {
    setAccountPage(1);
  }, [debouncedSearch, tab]);

  const catalogFetching =
    tab === "document"
      ? isCatalogVariableFetch(documentsQuery.networkStatus)
      : tab === "categorize"
        ? isCatalogVariableFetch(chartedAccountsQuery.networkStatus)
        : tab === "transactions"
          ? isCatalogVariableFetch(transactionsQuery.networkStatus)
          : false;

  const catalogDataReady = !catalogFetching;

  const catalogItems = useMemo((): TriageOptionListItem[] => {
    if (!catalogDataReady) {
      return [];
    }

    if (tab === "document") {
      const edges = documentsQuery.data?.documentsConnection?.data?.edges ?? [];
      return edges
        .map((edge) => edge.node)
        .map((document) =>
          documentToTriageListItem(
            document,
            selections.documentIdentifiers,
            triage.amount != null ? Number(triage.amount) : null,
            triage.effectiveDate,
          ),
        );
    }

    if (tab === "categorize") {
      const accounts = chartedAccountsQuery.data?.chartedAccounts?.data ?? [];
      const normalizedSearch = debouncedSearch.trim().toLowerCase();
      const filtered = accounts.filter((account) => {
        if (!account.identifier) {
          return false;
        }
        if (!normalizedSearch) {
          return true;
        }
        const name = account.name?.toLowerCase() ?? "";
        const path = account.fullPath?.join(" / ").toLowerCase() ?? "";
        const description = account.description?.toLowerCase() ?? "";
        const categoryNames = (account.categories ?? [])
          .map((category) => category.name?.toLowerCase() ?? "")
          .join(" ");
        return (
          name.includes(normalizedSearch) ||
          path.includes(normalizedSearch) ||
          description.includes(normalizedSearch) ||
          categoryNames.includes(normalizedSearch)
        );
      });
      return filtered
        .slice(0, accountPage * PAGE_SIZE)
        .map((account) =>
          accountToListItem(account, selections.chartedAccountIdentifier),
        );
    }

    if (tab === "transactions") {
      const edges =
        transactionsQuery.data?.transactionsConnection?.data?.edges ?? [];
      return edges
        .map((edge) => edge.node)
        .filter((transaction) => transaction.identifier !== triage.identifier)
        .map((transaction) =>
          transactionToTriageListItem(
            transaction,
            selections.transactionIdentifiers ?? [],
            triage.amount != null ? Number(triage.amount) : null,
            triage.effectiveDate,
          ),
        );
    }

    return [];
  }, [
    accountPage,
    catalogDataReady,
    chartedAccountsQuery.data,
    debouncedSearch,
    documentsQuery.data,
    selections.chartedAccountIdentifier,
    selections.documentIdentifiers,
    selections.transactionIdentifiers,
    tab,
    transactionsQuery.data,
    triage.amount,
    triage.effectiveDate,
    triage.identifier,
  ]);

  useLayoutEffect(() => {
    if (tab !== "document") {
      return;
    }
    const edges = documentsQuery.data?.documentsConnection?.data?.edges ?? [];
    if (edges.length === 0) {
      return;
    }
    registerCatalogDocuments(edges.map((edge) => edge.node));
  }, [documentsQuery.data, registerCatalogDocuments, tab]);

  useLayoutEffect(() => {
    if (tab !== "categorize") {
      return;
    }
    const accounts = chartedAccountsQuery.data?.chartedAccounts?.data ?? [];
    if (accounts.length === 0) {
      return;
    }
    registerCatalogChartedAccounts(
      accounts.flatMap((account) =>
        account.identifier
          ? [
              {
                id: account.id,
                identifier: account.identifier,
                name: account.name,
                path: account.path,
                fullPath: account.fullPath,
                description: account.description,
                categories: account.categories,
              },
            ]
          : [],
      ),
    );
  }, [chartedAccountsQuery.data, registerCatalogChartedAccounts, tab]);

  useLayoutEffect(() => {
    if (tab !== "transactions") {
      return;
    }
    const edges =
      transactionsQuery.data?.transactionsConnection?.data?.edges ?? [];
    if (edges.length === 0) {
      return;
    }
    registerCatalogTransactions(edges.map((edge) => edge.node));
  }, [registerCatalogTransactions, tab, transactionsQuery.data]);

  const catalogLoading = !catalogDataReady;

  const catalogLoadingMore =
    tab === "document"
      ? documentsQuery.networkStatus === NetworkStatus.fetchMore
      : tab === "transactions"
        ? transactionsQuery.networkStatus === NetworkStatus.fetchMore
        : false;

  const hasMore = useMemo(() => {
    if (tab === "document") {
      return (
        documentsQuery.data?.documentsConnection?.data?.pageInfo.hasNextPage ??
        false
      );
    }
    if (tab === "categorize") {
      const accounts = chartedAccountsQuery.data?.chartedAccounts?.data ?? [];
      const normalizedSearch = debouncedSearch.trim().toLowerCase();
      const filteredCount = accounts.filter((account) => {
        if (!account.identifier) {
          return false;
        }
        if (!normalizedSearch) {
          return true;
        }
        const name = account.name?.toLowerCase() ?? "";
        const path = account.fullPath?.join(" / ").toLowerCase() ?? "";
        const description = account.description?.toLowerCase() ?? "";
        const categoryNames = (account.categories ?? [])
          .map((category) => category.name?.toLowerCase() ?? "")
          .join(" ");
        return (
          name.includes(normalizedSearch) ||
          path.includes(normalizedSearch) ||
          description.includes(normalizedSearch) ||
          categoryNames.includes(normalizedSearch)
        );
      }).length;
      return accountPage * PAGE_SIZE < filteredCount;
    }
    if (tab === "transactions") {
      return (
        transactionsQuery.data?.transactionsConnection?.data?.pageInfo
          .hasNextPage ?? false
      );
    }
    return false;
  }, [
    accountPage,
    chartedAccountsQuery.data,
    debouncedSearch,
    documentsQuery.data,
    tab,
    transactionsQuery.data,
  ]);

  const loadMore = useCallback(() => {
    if (catalogLoadingMore) {
      return;
    }
    if (tab === "document") {
      const connection = documentsQuery.data?.documentsConnection?.data;
      if (!connection?.pageInfo.hasNextPage) {
        return;
      }
      void documentsQuery.fetchMore({
        variables: {
          entityIdentifiers,
          first: PAGE_SIZE,
          after: connection.pageInfo.endCursor,
          filter: documentCatalogFilter(debouncedSearch),
        },
      });
      return;
    }
    if (tab === "categorize") {
      setAccountPage((current) => current + 1);
      return;
    }
    if (tab === "transactions") {
      const connection = transactionsQuery.data?.transactionsConnection?.data;
      if (!connection?.pageInfo.hasNextPage) {
        return;
      }
      void transactionsQuery.fetchMore({
        variables: {
          entityIdentifiers,
          first: PAGE_SIZE,
          after: connection.pageInfo.endCursor,
          filter: transactionCatalogFilter(debouncedSearch),
        },
      });
    }
  }, [
    catalogLoadingMore,
    debouncedSearch,
    documentsQuery,
    entityIdentifiers,
    tab,
    transactionsQuery,
  ]);

  const listTestId =
    tab === "document"
      ? "triage-document-options"
      : tab === "categorize"
        ? "triage-categorize-options"
        : "triage-transaction-options";

  const listItems = catalogItems;

  // Its dialog has the keys while it is open, however it was opened: on its
  // first visible row once there is one, and again when what it lists no
  // longer has the row they were on.
  useEffect(() => {
    const firstKey = listItems[0]?.key ?? null;
    if (!firstKey) {
      return;
    }
    if (activeOptionKey == null) {
      setActiveOptionKey(firstKey);
      return;
    }
    const visibleKeys = new Set(listItems.map((item) => item.key));
    if (!visibleKeys.has(activeOptionKey)) {
      setActiveOptionKey(firstKey);
    }
  }, [activeOptionKey, listItems, setActiveOptionKey, tab]);

  return (
    <div className="h-full min-h-0 w-full flex flex-col overflow-hidden">
      <div className="flex-1 min-h-0 w-full flex flex-col">
        <TriageOptionList
          tab={tab}
          items={listItems}
          loading={catalogLoading}
          loadMore={
            hasMore
              ? {
                  hasMore: true,
                  loading: catalogLoadingMore,
                  onLoadMore: loadMore,
                }
              : undefined
          }
          emptyState={{
            title: debouncedSearch.trim() ? "No results" : "No items",
            description: debouncedSearch.trim()
              ? "Try a different search term."
              : undefined,
          }}
          data-testid={listTestId}
        />
      </div>
    </div>
  );
}
